import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  providerAuthService,
  Provider,
  ProviderSignupData,
  ProviderLoginData,
  ProviderAccountStatus,
  ProviderInductionProgress,
  ProviderInductionStatus,
  ProviderInductionChapterKey,
} from '@lib/supabase/providerAuth.service'
import { useAuth } from '@contexts/AuthContext'
import { validateEmail, validatePassword, validatePhoneNumber } from '@lib/validations'

interface UseProviderAuthReturn {
  provider: Provider | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  login: (data: ProviderLoginData) => Promise<{ success: boolean; error?: string; redirectTo?: string }>
  signup: (data: ProviderSignupData) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  verifyEmail: (email: string, token: string) => Promise<{ success: boolean; error?: string }>
  resendVerificationCode: (email: string) => Promise<{ success: boolean; error?: string }>
  submitDocuments: (cert: File, govId: File) => Promise<{ success: boolean; error?: string }>
  getAccountStatus: () => Promise<ProviderAccountStatus | null>
  getInductionStatus: () => Promise<ProviderInductionStatus>
  getInductionProgress: () => Promise<{ success: boolean; progress?: ProviderInductionProgress; error?: string }>
  saveInductionProgress: (currentChapter: number, currentStep: number) => Promise<{ success: boolean; progress?: ProviderInductionProgress; error?: string }>
  acknowledgeInductionChapter: (chapter: ProviderInductionChapterKey, currentChapter: number, currentStep: number) => Promise<{ success: boolean; progress?: ProviderInductionProgress; error?: string }>
  completeInduction: () => Promise<{ success: boolean; progress?: ProviderInductionProgress; error?: string }>
  refreshProvider: () => Promise<void>

  validateEmail: (email: string) => boolean
  validatePassword: typeof validatePassword
  validatePhoneNumber: (phone: string) => boolean
}

export function useProviderAuth(): UseProviderAuthReturn {
  const [error, setError] = useState<string | null>(null)
  const { session, role } = useAuth()
  const queryClient = useQueryClient()
  const providerProfileQueryKey = ['auth', 'provider-profile', session?.user.id ?? 'anonymous'] as const
  const isProviderProfileEnabled = !!session && role === 'provider'

  const refreshProvider = useCallback(async () => {
    if (!isProviderProfileEnabled) {
      queryClient.removeQueries({ queryKey: ['auth', 'provider-profile'] })
      return
    }

    await queryClient.invalidateQueries({ queryKey: providerProfileQueryKey })
  }, [isProviderProfileEnabled, providerProfileQueryKey, queryClient])

  const providerProfileQuery = useQuery({
    queryKey: providerProfileQueryKey,
    queryFn: providerAuthService.getProfile,
    enabled: isProviderProfileEnabled,
  })

  const provider = isProviderProfileEnabled ? providerProfileQuery.data ?? null : null
  const isLoading = isProviderProfileEnabled ? providerProfileQuery.isPending : false

  const login = useCallback(async (data: ProviderLoginData) => {
    setError(null)
    const result = await providerAuthService.login(data)

    if (!result.success) {
      return { success: false, error: result.error }
    }

    if (result.provider) {
      queryClient.setQueryData(['auth', 'provider-profile', result.provider.auth_id], result.provider)
    }

    const profile = result.provider
    if (!profile) return { success: true, redirectTo: '/provider/dashboard' }

    let redirectTo = '/provider/dashboard'

    if (!profile.is_verified) {
      redirectTo = `/auth/provider-email-verification?email=${encodeURIComponent(data.email)}`
    } else if (profile.account_status === 'document_pending') {
      redirectTo = '/auth/provider-identity-verification'
    } else if (profile.account_status === 'pending_approval') {
      redirectTo = '/auth/provider-verification-pending'
    } else if (profile.account_status === 'approved') {
      redirectTo = '/provider/dashboard'
    } else if (profile.account_status === 'rejected') {
      redirectTo = '/auth/provider-identity-verification'
    }

    return { success: true, redirectTo }
  }, [queryClient])

  const signup = useCallback(async (data: ProviderSignupData) => {
    setError(null)
    const result = await providerAuthService.signup(data)
    if (!result.success) {
      return { success: false, error: result.error }
    }
    return { success: true }
  }, [])

  const logout = useCallback(async () => {
    await providerAuthService.logout()
    queryClient.removeQueries({ queryKey: ['auth', 'provider-profile'] })
  }, [queryClient])

  const verifyEmail = useCallback(async (email: string, token: string) => {
    const result = await providerAuthService.verifyEmail(email, token)
    if (result.success) await refreshProvider()
    return result
  }, [refreshProvider])

  const resendVerificationCode = useCallback(async (email: string) => {
    return providerAuthService.resendVerificationEmail(email)
  }, [])

  const submitDocuments = useCallback(async (cert: File, govId: File) => {
    const result = await providerAuthService.submitDocuments(cert, govId)
    if (result.success) await refreshProvider()
    return result
  }, [refreshProvider])

  const getAccountStatus = useCallback(async () => {
    return providerAuthService.getAccountStatus()
  }, [])

  const getInductionStatus = useCallback(async () => {
    return providerAuthService.getInductionStatus()
  }, [])

  const getInductionProgress = useCallback(async () => {
    return providerAuthService.getOrCreateInductionProgress()
  }, [])

  const saveInductionProgress = useCallback(async (currentChapter: number, currentStep: number) => {
    return providerAuthService.saveInductionProgress(currentChapter, currentStep)
  }, [])

  const acknowledgeInductionChapter = useCallback(async (
    chapter: ProviderInductionChapterKey,
    currentChapter: number,
    currentStep: number,
  ) => {
    return providerAuthService.acknowledgeInductionChapter(chapter, currentChapter, currentStep)
  }, [])

  const completeInduction = useCallback(async () => {
    return providerAuthService.completeInduction()
  }, [])

  return {
    provider,
    isAuthenticated: !!provider,
    isLoading,
    error,
    login,
    signup,
    logout,
    verifyEmail,
    resendVerificationCode,
    submitDocuments,
    getAccountStatus,
    getInductionStatus,
    getInductionProgress,
    saveInductionProgress,
    acknowledgeInductionChapter,
    completeInduction,
    refreshProvider,
    validateEmail,
    validatePassword,
    validatePhoneNumber,
  }
}
